import { CouponContext } from '../components/Context/CouponContext';
import { ICoupon } from '../../../api/coupons/interfaces/couponInterface';
import '../../../_metronic/_assets/plugins/flaticon/flaticon.css';
import '../../../_metronic/_assets/plugins/flaticon2/flaticon.css';
import { useParams, useSearchParams } from 'react-router-dom';
import { useContext, useEffect, useRef, useState } from 'react';
import Card from '@mui/material/Card';

import { useFbPixel } from '../../../hooks/FbPixel';
import { useQuery } from 'react-query';
import { plansApi } from '../../../api/plans';

import { WhatsAppWidget } from '../../../components/widgets/WhatsAppWidget';
import { ExitIntentPopup } from '../components/ExitIntent';
import { apiFactory } from '../../../api/ApiFactory';
import { ContentContext } from '../components/Context/ContentContext';
import { PlanContext } from '../components/Context/PlanContext';
import { Sidebar } from '../components/Sidebar';
import { Form } from '../components/Form';
import {
	PageAsideBlocks,
	PageBlocks,
	PageFooterBlocks,
	useCheckoutPage
} from '../components/PageBlocks';
import {
	CheckoutPageContext,
	CheckoutPageSlotsContext
} from '../components/Context/CheckoutPageContext';
import { isPreviewMode } from '../components/PageBlocks/use-preview-bridge';
import { useCheckoutTracking } from '../components/PageBlocks/tracking';
import { useGTM } from '../../../hooks/GTM';
import { useGoogleAnalytics } from '../../../hooks/Analytics';

import axios from 'axios';
import { IPlan } from '../../../api/plans/interfaces/planInterface';
import { BillingType } from '../components/constants/billingType';
import { SubscriptionContext } from '../components/Context/SubscriptionContext';
import { ISubscription } from '../../../api/subscriptions/types/subscription.type';
import { checkoutsApi } from '../../../api/checkouts';
import { Row } from 'react-bootstrap';
import { ApplicationContext } from '../../../contexts/ApplicationContext';
import { useNavigate } from 'react-router-dom';
import { useBlockedPlatform } from '../../../hooks/use-blocked-platform';
import { BlockedLink } from '../BlockedLink';
import { PaymentsUnavailable } from '../PaymentsUnavailable';
import { arePaymentsUnavailable } from '../../../utils/payments-availability';
import { useCheckoutLinkGate } from '../components/hooks/useCheckoutLinkGate';
import { useCheckoutThemeReady } from '../components/hooks/usePageReady';
import { trackCheckoutOpen } from '../../../shared/waid-analytics';
import {
	useFinanceReady,
	waitsForFinance
} from '../../../hooks/use-finance-ready';
import { useCurseducaPixel } from '../../../hooks/CurseducaPixel';
import { CURSEDUCA_PLANS_REFERENCES } from '../components/Form';

interface IParams extends Record<string, string> {
	reference: string;
	platform: string;
}

const { list } = plansApi;
const { findByPlan } = checkoutsApi;

export const RecurringCheckout = () => {
	const navigate = useNavigate();

	const [plans, setPlans] = useState<IPlan[]>();
	const [currentPlan, setCurrentPlan] = useState<IPlan>();
	// Parcelas escolhidas no formulário — o resumo mostra o total com o desconto à vista em 1x.
	const [installmentCount, setInstallmentCount] = useState<number>(1);
	const [subscription, setSubscription] = useState<ISubscription | null>(
		null
	);

	const { reference, platform } = useParams<IParams>() as IParams;
	const { trackInitiateCheckout } = useFbPixel();
	const { pageView, selectItem } = useGoogleAnalytics();
	useGTM();
	useBlockedPlatform();

	const [currentPrice, setCurrentPrice] = useState<number>(0);

	const [billingType, setBillingType] = useState(BillingType.CreditCard);
	// Cupom de plataforma aplicado no plano (Waid Pay v2) — o resumo mostra o
	// desconto e o `useSubmit` manda o código na contratação.
	const [coupon, setCoupon] = useState<ICoupon | null>(null);

	const {
		data: plansData,
		isLoading,
		isError: isPlansError
	} = useQuery([list.key, { reference }], list.fetch, {
		refetchOnWindowFocus: false
	});

	const { finance } = useContext(ApplicationContext);
	// O formulário só aparece com o params (ou passados 4 s), como na Single:
	// sem isso o comprador começava a preencher e a página virava "pagamentos
	// indisponíveis" na frente dele. Plano grátis não trava, então não espera.
	const isFinanceReady = useFinanceReady(
		finance,
		waitsForFinance(currentPlan?.price, isPreviewMode())
	);

	useEffect(() => {
		// Ver comentário em Single: no preview do builder não redirecionamos.
		if (isPreviewMode()) return;

		if (plansData && plansData.data.length === 0) {
			navigate('/unavailable');
		}

		if (!finance) return;
		if (finance.isCurseducaPayConfigured === true) return;

		if (!currentPlan) return;
		if (currentPlan.price === 0) return;

		navigate('/unavailable');
	}, [finance, currentPlan]);

	useEffect(() => {
		setPlans(plansData?.data);
	}, [plansData]);

	useEffect(() => {
		pageView();
	}, [pageView]);

	useEffect(() => {
		trackInitiateCheckout();
	}, [trackInitiateCheckout]);

	// Waid Analytics: the checkout was landed on. Once per page load, with
	// the plan the page opened on - switching plans inside a price family
	// is not another open. The builder preview is not a visit.
	const checkoutOpened = useRef(false);
	useEffect(() => {
		if (checkoutOpened.current || isPreviewMode() || !currentPlan) return;
		checkoutOpened.current = true;
		void trackCheckoutOpen({
			type: 'subscription',
			plan: { id: String(currentPlan.id), name: currentPlan.name }
		});
	}, [currentPlan]);

	useEffect(() => {
		const currentPlan = plans?.find((plan) => plan.reference === reference);

		setCurrentPlan(currentPlan ? currentPlan : plans?.[0]);
	}, [plans]);

	useEffect(() => {
		setCurrentPrice(currentPlan?.price || 0);

		if (!currentPlan) return;
		selectItem({
			item: {
				id: currentPlan?.id,
				name: currentPlan?.name,
				price: currentPlan?.price
			}
		});
	}, [currentPlan]);

	const [platformApiKey, setPlatformApiKey] = useState('');

	useEffect(() => {
		apiFactory.isDefaultApiKeyDefined().then((response) => {
			if (response) return;

			axios
				.get<{ key: string }>(
					`${process.env.REACT_APP_CURSEDUCA_APPLICATION_DOMAIN}/platforms/slug/${platform}`
				)
				.then((response) => {
					apiFactory.enableCustom(response.data.key);
					setPlatformApiKey(response.data.key);
				});
		});

		return () => apiFactory.enableDefault();
	}, []);

	// Família de preços: a página customizada (builder) é a do plano PAI,
	// para o layout não trocar quando o comprador alterna a periodicidade.
	const pageOwnerPlanId = currentPlan?.familyParentId ?? currentPlan?.id;

	const { data: checkout, isLoading: isCheckoutLoading } = useQuery(
		[findByPlan.key, { id: pageOwnerPlanId }],
		findByPlan.fetch,
		{
			refetchOnWindowFocus: false
		}
	);

	// Página customizada do checkout (builder do hub) — vazia = layout padrão.
	const page = useCheckoutPage(checkout?.id);
	// Pixels da etapa de checkout (inertes no preview do builder).
	useCheckoutTracking(page?.settings?.tracking, 'checkout');

	const [searchParams] = useSearchParams();
	const hash = searchParams.get('hash') || undefined;
	// Skeleton até o tema/aparência da página do builder chegar: sem isso o
	// checkout aparece no layout padrão e troca pelo do admin na frente do
	// comprador.
	const isThemeReady = useCheckoutThemeReady(
		checkout?.id,
		!isPlansError && (isLoading || !currentPlan || isCheckoutLoading)
	);
	const linkGate = useCheckoutLinkGate({
		checkout,
		hash,
		// The checkout query only starts once a plan is picked, so the plan
		// chain counts as "still resolving" too. A failed plans request is
		// NOT resolving — it would never settle and would pin the skeleton
		// forever; fall back to the previous behaviour instead.
		isCheckoutResolving:
			!isPlansError && (isLoading || !currentPlan || isCheckoutLoading)
	});

	// Commercial links reuse the same plan references, but carry ?origin=COMERCIAL.
	const isCurseducaSelfCheckout =
		!!currentPlan &&
		!!CURSEDUCA_PLANS_REFERENCES[currentPlan.reference] &&
		searchParams.get('origin')?.toUpperCase() !== 'COMERCIAL';

	const { track: trackCurseduca } = useCurseducaPixel(
		isCurseducaSelfCheckout
	);

	useEffect(() => {
		if (!isCurseducaSelfCheckout) return;

		trackCurseduca('PageView');
		trackCurseduca('InitiateCheckout');
	}, [isCurseducaSelfCheckout]);

	// Mounted while the gate is still resolving as well: an invalid single-use
	// link must never render the form before redirecting.
	if (
		linkGate.isResolving ||
		linkGate.isBlocked ||
		!isThemeReady ||
		!isFinanceReady
	) {
		return (
			<BlockedLink
				isResolving={linkGate.isResolving || !linkGate.isBlocked}
			/>
		);
	}

	// Vendedor em análise ou pausado: o estado no lugar do formulário, para o
	// comprador não preencher tudo e descobrir no fim (pay #619).
	if (
		arePaymentsUnavailable(finance, {
			isFree: currentPlan?.price === 0,
			isPreview: isPreviewMode()
		})
	) {
		return <PaymentsUnavailable checkoutId={checkout?.id} />;
	}

	return (
		<CheckoutPageContext.Provider value={page?.settings ?? {}}>
			<CheckoutPageSlotsContext.Provider value={page?.slots ?? {}}>
				<ContentContext.Provider
					value={{
						currentPrice,
						quantity: 1,
						setQuantity: () => false,
						setCurrentPrice,
						isLoading,
						// DINÂMICO, não fixo em cartão: é o que faz o Pix (e o Pix
						// Automático) funcionarem na assinatura. Fixar aqui matava
						// a escolha de método antes de ela chegar ao formulário.
						billingType,
						setBillingType,
						checkout
					}}
				>
					<PlanContext.Provider
						value={{
							plan: currentPlan,
							plans,
							setPlan: setCurrentPlan,
							installmentCount,
							setInstallmentCount
						}}
					>
						<SubscriptionContext.Provider
							value={{ subscription, setSubscription }}
						>
							<CouponContext.Provider
								value={{ coupon, setCoupon }}
							>
								<div
									style={{
										backgroundImage: `url(${checkout?.backgroundImage})`,
										backgroundSize: 'cover'
									}}
									className="d-flex flex-column align-items-center"
								>
									<div className="content w-100">
										<div className="container">
											{checkout?.headerImage && (
												<Row>
													<img
														className="w-100 h-100"
														src={
															checkout.headerImage
														}
													/>
												</Row>
											)}
											<PageBlocks
												page={page}
												checkoutId={checkout?.id}
											/>
											<div className="checkout-grid">
												<Card className="rounded-xl p-md-10 form-grid-item">
													<Form
														apiKey={platformApiKey}
													/>
												</Card>
												<div className="sidebar-grid-item">
													<Sidebar />
													<PageAsideBlocks
														page={page}
														checkoutId={
															checkout?.id
														}
													/>
												</div>
												<PageFooterBlocks
													page={page}
													checkoutId={checkout?.id}
												/>
											</div>
										</div>
									</div>
								</div>
								<WhatsAppWidget />
								{/* Assinatura não tem campo de cupom: o popup entra
							    sem ele, para não prometer um desconto que não
							    há onde aplicar. */}
								<ExitIntentPopup
									settings={
										page?.settings?.exitIntent
											? {
													...page.settings.exitIntent,
													couponCode: undefined
											  }
											: undefined
									}
								/>
							</CouponContext.Provider>
						</SubscriptionContext.Provider>
					</PlanContext.Provider>
				</ContentContext.Provider>
			</CheckoutPageSlotsContext.Provider>
		</CheckoutPageContext.Provider>
	);
};
